import { zodTransformFirestoreTimestampToDate } from "../utils/zodUtils.mjs";
import { zodFlavorId } from "./ids.mjs";
import { defineFirestoreCollection } from "./firebaseCollectionSchema.mjs";
import { PiiCategory, meta } from "./meta.mjs";
import { z as z$1 } from "zod";

// src/models/passwordRecoveryBridge.ts
const PASSWORD_RECOVERY_BRIDGE_TTL_PRESETS = [
	"1h",
	"12h",
	"1d",
	"2d",
	"1w"
];
const PASSWORD_RECOVERY_BRIDGE_STATUSES = [
	"active",
	"used",
	"revoked"
];
const passwordRecoveryBridgeActorSchema = z$1.object({
	adminUid: meta(z$1.string(), { pii: { category: "identifier" } }),
	adminEmail: meta(z$1.string(), { pii: { category: "email" } })
});
const passwordRecoveryBridgeRedeemedMetadataSchema = z$1.object({
	deviceClass: meta(z$1.string().optional(), { pii: { category: "deviceId" } }),
	browser: meta(z$1.string().optional(), { pii: { category: "deviceId" } }),
	os: meta(z$1.string().optional(), { pii: { category: "deviceId" } })
});
const passwordRecoveryBridgeSchema = z$1.object({
	id: zodFlavorId(),
	bridgeId: zodFlavorId(),
	customerId: zodFlavorId(),
	salt: meta(z$1.string(), { pii: { category: "credential" } }),
	secretHash: meta(z$1.string(), { pii: { category: "credential" } }),
	hashVersion: z$1.literal(1),
	createdAt: z$1.any().transform(zodTransformFirestoreTimestampToDate).pipe(z$1.date()),
	expiresAt: z$1.any().transform(zodTransformFirestoreTimestampToDate).pipe(z$1.date()),
	ttlPreset: z$1.enum(PASSWORD_RECOVERY_BRIDGE_TTL_PRESETS),
	status: z$1.enum(PASSWORD_RECOVERY_BRIDGE_STATUSES),
	createdBy: passwordRecoveryBridgeActorSchema,
	usedAt: z$1.any().transform(zodTransformFirestoreTimestampToDate).pipe(z$1.date()).optional(),
	revokedAt: z$1.any().transform(zodTransformFirestoreTimestampToDate).pipe(z$1.date()).optional(),
	revokedBy: passwordRecoveryBridgeActorSchema.optional(),
	redeemedMetadata: passwordRecoveryBridgeRedeemedMetadataSchema.optional()
});
const PasswordRecoveryBridges = defineFirestoreCollection("password_recovery_bridges", passwordRecoveryBridgeSchema, void 0, {
	dwReplication: true,
	firestoreCollectionPath: "password_recovery_bridges"
});
const customerAuthDisabledBySchema = passwordRecoveryBridgeActorSchema;
/** Callable request/response shapes shared by admin UI and functions. */
const createPasswordRecoveryBridgeRequestSchema = z$1.object({
	customerId: zodFlavorId(),
	ttlPreset: z$1.enum(PASSWORD_RECOVERY_BRIDGE_TTL_PRESETS).default("1d")
});
const regeneratePasswordRecoveryBridgeRequestSchema = z$1.object({
	customerId: zodFlavorId(),
	ttlPreset: z$1.enum(PASSWORD_RECOVERY_BRIDGE_TTL_PRESETS).default("1d")
});
const revokePasswordRecoveryBridgeRequestSchema = z$1.object({
	bridgeId: zodFlavorId(),
	reason: meta(z$1.string().optional(), { pii: { category: "freeText" } })
});
const setCustomerAuthDisabledRequestSchema = z$1.object({
	customerId: zodFlavorId(),
	disabled: z$1.boolean(),
	reason: meta(z$1.string().max(1e3).optional(), { pii: { category: "freeText" } })
}).superRefine((data, ctx) => {
	if (data.disabled && (!data.reason || data.reason.trim().length === 0)) ctx.addIssue({
		code: "custom",
		message: "Reason is required when disabling account",
		path: ["reason"]
	});
});
const revokeCustomerSessionsRequestSchema = z$1.object({ customerId: zodFlavorId() });
const getCustomerAuthStatusRequestSchema = z$1.object({ customerId: zodFlavorId() });
const redeemPasswordRecoveryBridgeRequestSchema = z$1.object({ token: meta(z$1.string().trim().min(1).max(500), { pii: { category: "credential" } }) });

export { PASSWORD_RECOVERY_BRIDGE_STATUSES, PASSWORD_RECOVERY_BRIDGE_TTL_PRESETS, PasswordRecoveryBridges, createPasswordRecoveryBridgeRequestSchema, customerAuthDisabledBySchema, getCustomerAuthStatusRequestSchema, passwordRecoveryBridgeActorSchema, passwordRecoveryBridgeRedeemedMetadataSchema, passwordRecoveryBridgeSchema, redeemPasswordRecoveryBridgeRequestSchema, regeneratePasswordRecoveryBridgeRequestSchema, revokeCustomerSessionsRequestSchema, revokePasswordRecoveryBridgeRequestSchema, setCustomerAuthDisabledRequestSchema };
//# sourceMappingURL=passwordRecoveryBridge.mjs.map